import { useNow } from '@vueuse/core'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import minMax from 'dayjs/plugin/minMax'
import quarterOfYear from 'dayjs/plugin/quarterOfYear'
import isBetween from 'dayjs/plugin/isBetween'
import { ApiLocale, Locale } from '@library/enums'
import { joinText } from '@library/utils/functions'
import { useLocale } from './use-locale'

type DateFormatType = 'EN' | 'ZH'

dayjs.extend(quarterOfYear)
dayjs.extend(isSameOrAfter)
dayjs.extend(isSameOrBefore)
dayjs.extend(minMax)
dayjs.extend(isBetween)

/** 日期格式化工具 */
export function useDate() {
  const typeSetting: Record<Locale, DateFormatType> = {
    [Locale.ZH_TW]: 'ZH',
    [Locale.ZH_CN]: 'ZH',
    [Locale.EN]: 'EN'
  }

  const { i18n } = useLocale()

  const { locale, t } = i18n.global

  /** API 日期格式字串 */
  const apiDateFormat = 'YYYY/MM/DD'
  /** API 日期時間格式字串 */
  const apiDateTimeFormat = 'YYYY/MM/DD HH:mm:ss'
  /** API 月份格式字串 */
  const apiMonthDateFormat = 'YYYY/MM'
  /** API 年份格式字串 */
  const apiYearFormat = 'YYYY'
  /** 日期格式 */
  const dateFormat = computed(() => t('Library.Common.DateFormat.Date'))
  /** 完整日期格式 */
  const fullDateFormat = computed(() => t('Library.Common.DateFormat.FullDate'))
  /** 完整月份格式 */
  const fullMonthFormat = computed(() =>
    t('Library.Common.DateFormat.FullMonth')
  )
  /** 完整時間格式 */
  const fullTimeFormat = computed(() => t('Library.Common.DateFormat.FullTime'))
  /** 時間格式 */
  const timeFormat = computed(() => t('Library.Common.DateFormat.Time'))
  /** 月份格式 */
  const monthFormat = computed(() => t('Library.Common.DateFormat.Month'))
  /** 年份格式 */
  const yearFormat = computed(() => t('Library.Common.DateFormat.Year'))
  /** AM/PM 格式 */
  const amPmFormat = computed(() => t('Library.Common.DateFormat.AmPm'))

  /** 當前時間 - Date 資料 */
  const nowDate = useNow()

  /** 今天日期 */
  const today = computed(() => dayjs(nowDate.value).format(dateFormat.value))

  /** 當前時間 */
  const now = computed(() => dayjs(nowDate.value).format(fullTimeFormat.value))

  const nowFullDateFormat = computed(() =>
    dayjs(nowDate.value).format(fullDateFormat.value)
  )

  /** 一年前日期 */
  const oneYearAgo = computed(() =>
    dayjs(nowDate.value).subtract(1, 'year').format(dateFormat.value)
  )

  /** 格式化日期 */
  const formatDate = (
    date: string | Dayjs | null | undefined,
    /** 是否顯示日期的時分秒單位 */
    showDateTimeValue = false
  ): string | null => {
    if (!date) {
      return null
    }

    // 不顯示日期的時分秒單位
    if (!showDateTimeValue) {
      return dayjs(date).format(dateFormat.value)
    }

    if (typeof date !== 'string') {
      return date.format(
        showDateTimeValue ? timeFormat.value : dateFormat.value
      )
    }

    const [dateValue, timeValue] = date.split(' ')

    if (dateValue) {
      let formatDateValue = ''
      const [hour, minute] = timeValue?.split(':') ?? []
      const formatTimeValue = joinText([hour, minute], ':')

      const [year, month, day] = dateValue.split('/')

      if (day) {
        formatDateValue =
          typeSetting[locale.value] === ApiLocale.ZH_TW
            ? `${year}/${month}/${day}`
            : `${month}/${day}/${year}`
      } else {
        formatDateValue =
          typeSetting[locale.value] === ApiLocale.ZH_TW
            ? `${year}/${month}`
            : `${month}/${year}`
      }

      return joinText([formatDateValue, formatTimeValue], ' ')
    }

    return null
  }

  /** 字串轉 Dayjs 物件 */
  const toDate = (date: string, parse: string = apiDateFormat): Dayjs => {
    return dayjs(date, parse)
  }

  /** 取得月份 */
  const getMonth = (date: string): number => {
    return dayjs(date).month() + 1
  }

  return {
    /** dayjs 實例 */
    dayjs,
    /** AM/PM 格式 */
    amPmFormat,
    /** 日期格式 */
    dateFormat,
    /** 時間格式 */
    timeFormat,
    /** 完整時間格式 */
    fullTimeFormat,
    /** 完整月份格式 */
    fullMonthFormat,
    /** 月份格式 */
    monthFormat,
    /** 年份格式 */
    yearFormat,
    /** 格式化日期 */
    formatDate,
    /** 完整日期格式 */
    fullDateFormat,
    /** 字串轉 Dayjs 物件 */
    toDate,
    /** 當前時間 - Date 資料 */
    nowDate,
    /** 當前時間 */
    now,
    /** 當前完整日期時間 */
    nowFullDateFormat,
    /** 今天日期 */
    today,
    /** 一年前日期 */
    oneYearAgo,
    /** API 日期時間格式字串 */
    apiDateTimeFormat,
    /** API 日期格式字串 */
    apiDateFormat,
    /** API 月份格式字串 */
    apiMonthDateFormat,
    /** API 年份格式字串 */
    apiYearFormat,
    /** 取得月份 */
    getMonth
  }
}
